﻿import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGridApiContext,
  useGridSelector, 
  selectedGridRowsSelector  
} from "@mui/x-data-grid-pro";
import axios from "@/features/axiosHelper";
import {
  selectConnection,
  selectDateFilter,
  getWorkflows,
} from "@/features/homepage/homeSlice";
import { selectWorkflow, statusTypes } from "@/features/homepage/workflowManagerSessionSlice";
import "./HorizontalWorkflowMenu.css";
import { createAlert } from "@/features/homepage/alertSliceList";
import { fetchRecordsData } from "@/features/homepage/workflowManagerSlice";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import dayjs from "dayjs";

const HorizontalWorkflowMenu = ({ setLastRefreshedAt }) => {
  const apiRef = useGridApiContext();
  const dispatch = useDispatch();

  const selectedRows = useGridSelector(apiRef, selectedGridRowsSelector);

  const currentOperationId = useSelector(selectWorkflow);
  const currentDateFilter = useSelector(selectDateFilter);
  const currentConnectionId = useSelector(selectConnection);

  const getRecordsData = (clearCache = true) => {
    let startDate = currentDateFilter.startDate;
    let endDate = currentDateFilter.endDate;
    const defaultStartDays = 7;

      if (startDate === "" && endDate === "") {
          startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
          endDate = dayjs();
      } else if (endDate === null) {
          endDate = startDate;
      }
      endDate = endDate.add(1, "day"); // always set to end of day
    dispatch(
      fetchRecordsData({
        operationId: currentOperationId,
        startDate: startDate,
        endDate: endDate,
        connectionId:
          currentConnectionId === "All" ? -1 : currentConnectionId.ConnectionId,
        clearCache: clearCache,
      })
    );
    if (setLastRefreshedAt) {
      setLastRefreshedAt(dayjs());
    }
    dispatch(getWorkflows());
  };

  const validateRetry = (event) => {
    const rowData = Array.from(selectedRows.values());

    const hasSuccess = rowData.some(
      (row) => row.status === statusTypes.Successful
    );

    if (hasSuccess) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "Retrying successful records will create duplicate records. Do you wish to continue?",
          buttonLabel: "Retry",
          onButtonClick: () => handleRetry(event),
          buttonLabel2: "Cancel",
        })
      );
    } else {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Are you sure you want to Retry the record/(s)?",
          buttonLabel: "Retry",
          onButtonClick: () => handleRetry(event),
          buttonLabel2: "Cancel",
        })
      );
    }
  };

  const handleRetry = (event) => {
    event.stopPropagation();
    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be retried",
          })
        );
        return;
      }
    }

    const workflowRowActionDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
      OperationId: currentOperationId,
      SourceConnectionId: row.sourceConnectionId,
      ConnPairId: row.connectionPairId,
      PostedDate: row.toDate,
      CreatedDate: row.createdDate,
      TargetArtifactDictionaryDataId: row.targetArtifactDictionaryDataId,
      WorkflowRecordId: row.id,
      TargetConnectionId: row.targetConnectionId,
    }));

    axios
      .post("/operationsmanager/retry-workflow", workflowRowActionDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const validateProcess = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Process the record/(s)?",
        buttonLabel: "Process",
        onButtonClick: () => handleProcess(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleProcess = (event) => {
    event.stopPropagation();
    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be processed",
          })
        );
        return;
      }
    }

    const workflowRowDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
      OperationId: currentOperationId,
      SourceConnectionId: row.sourceConnectionId,
      ConnPairId: row.connectionPairId,
      WorkflowRecordId: row.id,
    }));

    axios
      .post("/operationsmanager/process-workflow", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const validateHold = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Hold the record/(s)?",
        buttonLabel: "Hold",
        onButtonClick: () => handleHold(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleHold = (event) => {
    event.stopPropagation();
    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be held",
          })
        );
        return;
      }
    }

    const workflowRowDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
    }));

    axios
      .post("/operationsmanager/hold-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const validateUnhold = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Unhold the record/(s)?",
        buttonLabel: "Unhold",
        onButtonClick: () => handleUnhold(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleUnhold = (event) => {
    event.stopPropagation();
    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be unheld",
          })
        );
        return;
      }
    }

    const workflowRowDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
    }));

    axios
      .post("/operationsmanager/unhold-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const validateArchive = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Archive the record/(s)?",
        buttonLabel: "Archive",
        onButtonClick: () => handleArchive(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleArchive = (event) => {
    event.stopPropagation();
    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be archived",
          })
        );
        return;
      }
    }

    const workflowRowDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
    }));

    axios
      .post("/operationsmanager/archive-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const validateRestore = (event) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: "Are you sure you want to Restore the record/(s)?",
        buttonLabel: "Restore",
        onButtonClick: () => handleRestore(event),
        buttonLabel2: "Cancel",
      })
    );
  };

  const handleRestore = (event) => {
    event.stopPropagation();

    const rowData = Array.from(selectedRows.values());

    if (!rowData?.length) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "single",
          message: "Please select one or multiple record/s.",
        })
      );
      return;
    }
    for (const row of rowData) {
      if (row?.connectionPairId === -1) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unassigned records cannot be restored",
          })
        );
        return;
      }
    }

    const workflowRowDTO = rowData.map((row) => ({
      SourceDictionaryDataId: row.sourceArtifactDictionaryDataId,
      Status: row.status,
      IsHeld: row.isHeld,
      IsArchived: row.isArchived,
      SourceReference: row.fromReference,
    }));

    axios
      .post("/operationsmanager/unarchive-workflows", workflowRowDTO, null)
      .then((response) => {
        if (response.data) {
          dispatch(
            createAlert({
              status: response.data.alert,
              alertType: response.data.alert === "Success" ? "single" : "result",
              message: response.data.message,
            })
          );
          getRecordsData();
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          })
        );
        console.error("Error applying option: ", error);
      });
  };

  const actions = [
    { label: "Retry", onClick: validateRetry },
    { label: "Process", onClick: validateProcess },
    { label: "Hold", onClick: validateHold },
    { label: "Unhold", onClick: validateUnhold },
    { label: "Archive", onClick: validateArchive, variant: "danger" },
    { label: "Restore", onClick: validateRestore },
  ];

  return <ShepHorizontalMenu actions={actions} />;
};

export default HorizontalWorkflowMenu;
